import { useCallback, useEffect } from 'react';

import { useMytaverse } from '@providers/MytaverseProvider';
import { CurrentScreenEnum } from '@providers/MytaverseProvider/interfaces';

import { useMytaStreams } from './MytaStreamsProvider';

// A reconnect re-mounts the loading cinema mid-visit, so replaying the loading stages over an established
// `world` would report a visit that reached the world as one that never did.
const SCREEN_RANK: CurrentScreenEnum[] = [
  CurrentScreenEnum.DEFAULT,
  CurrentScreenEnum.LOGIN,
  CurrentScreenEnum.AVATAR_SELECTION,
  CurrentScreenEnum.AVATURN,
  CurrentScreenEnum.DEVICES_TEST_MICROPHONE,
  CurrentScreenEnum.DEVICES_TEST_SPEAKER,
  CurrentScreenEnum.DEVICES_TEST_CONNECTION,
  CurrentScreenEnum.DEVICES_TEST_WEBCAM,
  CurrentScreenEnum.LOADING_STREAM,
  CurrentScreenEnum.LOADING_UE,
  CurrentScreenEnum.WORLD,
];

/**
 * Returns a screen setter that only ever moves forward, ignoring a stage at or below the current one.
 *
 * @returns `advanceScreen`, safe to call from an effect that re-runs on a reconnect.
 */
export const useAdvanceScreen = () => {
  const { currentScreen, setCurrentScreen } = useMytaverse();

  return useCallback(
    (next: CurrentScreenEnum) => {
      if (SCREEN_RANK.indexOf(next) <= SCREEN_RANK.indexOf(currentScreen)) {
        return;
      }

      setCurrentScreen(next);
    },
    [currentScreen, setCurrentScreen],
  );
};

/**
 * Reports myta-streams entry progress as `loadingStream` then `loadingUE`, the stages GameCastLoader
 * reports on its own path.
 */
export const useMytaStreamsStage = () => {
  const { leaseStatus, dataChannelOpened } = useMytaStreams();
  const advanceScreen = useAdvanceScreen();

  useEffect(() => {
    if (leaseStatus === 'active') {
      advanceScreen(CurrentScreenEnum.LOADING_STREAM);
    }
  }, [leaseStatus, advanceScreen]);

  useEffect(() => {
    if (dataChannelOpened) {
      advanceScreen(CurrentScreenEnum.LOADING_UE);
    }
  }, [dataChannelOpened, advanceScreen]);
};
